import React from "react";

import { Home2026HeroBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Cta } from "../../shared/Cta";
import { LogoMark } from "../../shared/LogoMark";
import { Mask } from "../../shared/Reveal";
import { GL_BG } from "../../shared/gl";
import { PageGlow } from "../../shared/glow";
import {
    REDUCED_MOTION_QUERY,
    usePrefersReducedMotion,
} from "../../shared/reducedMotion";
import { endingsSentence } from "./rolling";
import { smoothstep, useHeroScroll } from "./useHeroScroll";
import { INCOMING_ATTR, useRollingEnding } from "./useRollingEnding";

import styles from "./index.module.scss";

// The hero's part of the page intro (app.js INTRO), in ms from its start.
// The mark's own 380ms delay is in the stylesheet, so it is switched at 0.
const INTRO = {
    glowStart: 480,
    glowDuration: 1100,
    text: 1610,
    // The copy's .82s has landed: the mark hands over to its turn, and the
    // headline starts rolling
    done: 1610 + 1100,
};

type Props = Value & {
    /**
     * Start with the hero hidden and play its part of the page intro: the
     * mark turns in, the glow fades up, then the copy rises. Off, it renders
     * finished (the prototype's `?intro=0`).
     */
    intro?: boolean;
};

/**
 * The 2026 homepage hero: "Build your" plus a rolling list of endings, the
 * body and CTA, the logo mark that turns with every ending, and the glow
 * rising off the bottom of the viewport.
 *
 * The hero is fixed over the viewport and fades out as the page scrolls; an
 * in-flow spacer holds its place and gates the animation.
 */
export const Home2026HeroBlock = ({ intro = false, ...props }: Props) => {
    const phrases = React.useMemo(
        () =>
            (props.rotating_endings ?? [])
                .map((ending) => ending.value)
                .filter((value) => !!value),
        [props.rotating_endings],
    );

    const { rootRef, heroRef } = useHeroScroll();
    const lineRef = React.useRef<HTMLSpanElement>(null);
    const spacerRef = React.useRef<HTMLDivElement>(null);

    // The intro's stages. Armed from the first render when it plays, so the
    // server's markup doesn't flash the finished hero first.
    const [markIn, setMarkIn] = React.useState(!intro);
    const [textShown, setTextShown] = React.useState(!intro);
    const [introDone, setIntroDone] = React.useState(!intro);

    React.useLayoutEffect(() => {
        if (!intro) return;
        const setGlow = (value: number) =>
            rootRef.current?.style.setProperty(
                "--glow-intro",
                value.toFixed(3),
            );

        if (window.matchMedia(REDUCED_MOTION_QUERY).matches) {
            setGlow(1);
            /* eslint-disable react-hooks/set-state-in-effect */
            setMarkIn(true);
            setTextShown(true);
            setIntroDone(true);
            /* eslint-enable react-hooks/set-state-in-effect */
            return;
        }

        let raf = requestAnimationFrame(() => setMarkIn(true));
        const timers = [
            window.setTimeout(() => {
                const start = performance.now();
                const step = (now: number) => {
                    const k = Math.min(
                        1,
                        Math.max(0, (now - start) / INTRO.glowDuration),
                    );
                    setGlow(smoothstep(k));
                    if (k < 1) raf = requestAnimationFrame(step);
                };
                raf = requestAnimationFrame(step);
            }, INTRO.glowStart),
            window.setTimeout(() => setTextShown(true), INTRO.text),
            window.setTimeout(() => setIntroDone(true), INTRO.done),
        ];
        return () => {
            cancelAnimationFrame(raf);
            timers.forEach((id) => window.clearTimeout(id));
        };
    }, [intro]);

    // Only while the spacer the hero is pinned over is on screen: the hero
    // itself is fixed, so it never leaves the viewport
    const [visible, setVisible] = React.useState(false);
    React.useEffect(() => {
        const spacer = spacerRef.current;
        if (!spacer) return;
        const observer = new IntersectionObserver(
            (entries) => setVisible(entries[entries.length - 1].isIntersecting),
            { threshold: 0 },
        );
        observer.observe(spacer);
        return () => observer.disconnect();
    }, []);

    const reduced = usePrefersReducedMotion();
    const roll = useRollingEnding(
        phrases,
        lineRef,
        visible && introDone && !reduced,
    );

    const current = phrases.length
        ? phrases[roll.current % phrases.length]
        : null;
    const incoming = roll.incoming === null ? null : phrases[roll.incoming];

    return (
        <div
            ref={rootRef}
            className={concatClassNames([
                styles.root,
                intro ? styles.introGlow : "",
            ])}
        >
            <PageGlow
                variant="hero"
                className={styles.glow}
                phrase={roll.changes}
                observeRef={spacerRef}
            />
            <div ref={heroRef} className={styles.hero}>
                <div
                    className={concatClassNames([
                        styles.mark,
                        markIn ? styles.markIn : "",
                        introDone ? styles.markSettled : "",
                    ])}
                    style={{
                        "--spin": `${roll.changes * 45}deg`,
                        "--mark-fill": GL_BG,
                    }}
                    aria-hidden="true"
                >
                    <LogoMark
                        className={styles.markSvg}
                        dotClassName={styles.dot}
                    />
                </div>
                <div className={styles.band}>
                    <h1 className={styles.headline}>
                        <Mask
                            shown={textShown}
                            className={concatClassNames([
                                styles.line,
                                textShown ? "" : styles.armed,
                            ])}
                        >
                            Build your
                        </Mask>
                        {current && (
                            <>
                                <span
                                    ref={lineRef}
                                    className={concatClassNames([
                                        styles.line,
                                        styles.dyn,
                                        roll.changes > 0 ? styles.live : "",
                                        textShown ? "" : styles.armed,
                                    ])}
                                    aria-hidden="true"
                                >
                                    <span
                                        key={roll.current}
                                        className={
                                            roll.phase === "moving"
                                                ? styles.out
                                                : undefined
                                        }
                                    >
                                        {current}
                                    </span>
                                    {incoming != null && (
                                        <span
                                            key={roll.incoming}
                                            {...{ [INCOMING_ATTR]: "" }}
                                            className={concatClassNames([
                                                styles.in,
                                                roll.phase === "armed"
                                                    ? styles.inArmed
                                                    : "",
                                            ])}
                                        >
                                            {incoming}
                                        </span>
                                    )}
                                </span>
                                <span className={styles.sr}>
                                    {endingsSentence(phrases)}
                                </span>
                            </>
                        )}
                    </h1>
                    <div className={styles.aside}>
                        {props.body && (
                            <Mask shown={textShown}>
                                <RichTextBlock
                                    value={props.body}
                                    className={styles.body}
                                />
                            </Mask>
                        )}
                        {props.cta?.display_name && (
                            <Mask shown={textShown} cta>
                                <Cta variant="dark" link={props.cta}>
                                    {props.cta.display_name}
                                </Cta>
                            </Mask>
                        )}
                    </div>
                </div>
            </div>
            <div ref={spacerRef} className={styles.spacer} aria-hidden="true" />
        </div>
    );
};
